import cn from 'classnames';
import { FC, SyntheticEvent, useEffect, useState } from 'react';

import { VIDEO_POSTER_FRAGMENT } from '@/shared/config';
import { ImageSizeType } from '@/shared/config/types';
import { useAppSelector } from '@/shared/hooks';
import { getFileSrc, getImageSrc, getRotationCoverScale } from '@/shared/lib';
import { Icon } from '@/shared/ui';

import type { CropBox } from '@/entities/album';
import {
  hasPhotoTransformFields,
  mapViewOptionsToTransform,
} from '@/entities/album';
import { assetsModel } from '@/entities/assets';

import styles from './TransformedPhoto.module.scss';

type TransformedPhotoProps = {
  photoID: string;
  size: ImageSizeType;
  alt: string;
  className?: string;
  forceSquare?: boolean;
};

/**
 * Cover scale for a fixed-aspect-ratio container.
 *
 * `getRotationCoverScale` returns 1 at exact 90° multiples because
 * in the PhotoSlider the container adapts to the content. Here the
 * container has a fixed aspect-ratio, so a 90°/270° rotation swaps
 * the image dimensions and requires extra scaling to cover.
 */
const getCoverScaleForContainer = (
  rotation: number,
  imageAspectRatio: number,
): number => {
  const baseScale = getRotationCoverScale(rotation, imageAspectRatio);

  const nearest90 = Math.round(rotation / 90) * 90;
  const nearestMod = ((nearest90 % 360) + 360) % 360;

  if (nearestMod === 90 || nearestMod === 270) {
    const swapScale = Math.max(imageAspectRatio, 1 / imageAspectRatio);

    return Math.max(baseScale, swapScale);
  }

  return baseScale;
};

const buildTransformCSS = (
  rotation: number,
  flipHorizontal: boolean,
  totalScale: number,
  panX: number,
  panY: number,
): string => {
  const transforms: string[] = [];

  if (panX !== 0 || panY !== 0) {
    transforms.push(`translate(${panX * 100}%, ${panY * 100}%)`);
  }

  if (totalScale !== 1) {
    transforms.push(`scale(${totalScale})`);
  }

  if (rotation !== 0) {
    transforms.push(`rotate(${rotation}deg)`);
  }

  if (flipHorizontal) {
    transforms.push('scaleX(-1)');
  }

  return transforms.join(' ');
};

const buildCropImgStyle = (cropBox: CropBox): React.CSSProperties => {
  const imageWidth = 1 / cropBox.width;
  const cropCenterX = cropBox.x + cropBox.width / 2;
  const cropCenterY = cropBox.y + cropBox.height / 2;

  return {
    position: 'absolute' as const,
    width: `${imageWidth * 100}%`,
    left: `${50 - cropCenterX * imageWidth * 100}%`,
    top: '50%',
    transform: `translateY(-${cropCenterY * 100}%)`,
    maxWidth: 'none',
    maxHeight: 'none',
  };
};

export const TransformedPhoto: FC<TransformedPhotoProps> = (props) => {
  const { photoID, size, alt, className, forceSquare = false } = props;

  const [hasVideoError, setHasVideoError] = useState(false);

  const assetMediaKind = useAppSelector(
    assetsModel.selectors.selectAssetMediaKind(photoID),
  );

  const isVideoAsset = assetMediaKind === 'video';

  const videoFileID = useAppSelector(
    assetsModel.selectors.selectAssetVideoFileID(photoID),
  );

  const handleVideoError = (event: SyntheticEvent<HTMLVideoElement>) => {
    const code = event.currentTarget.error?.code;

    if (code === MediaError.MEDIA_ERR_ABORTED || code === undefined) {
      return;
    }

    setHasVideoError(true);
  };

  const transformData = useAppSelector((state) => {
    const asset = assetsModel.selectors.selectAssetByID(photoID)(state);

    if (asset?.Kind.case !== 'Study') {
      return undefined;
    }

    const viewOptions = asset.Kind.value.MedicalImageFeatures?.ViewOptionsV2;

    if (!viewOptions || !hasPhotoTransformFields(viewOptions)) {
      return undefined;
    }

    const content = asset.Kind.value.Content;

    if (content.case !== 'DentalPhoto') {
      return undefined;
    }

    const imageFile = content.value.Image;
    const imageMeta =
      imageFile?.Metadata.case === 'Image'
        ? imageFile.Metadata.value
        : undefined;

    if (!imageMeta || !imageMeta.Width || !imageMeta.Height) {
      return undefined;
    }

    const imageAspectRatio = Number(imageMeta.Width) / Number(imageMeta.Height);

    return mapViewOptionsToTransform(viewOptions, imageAspectRatio);
  });

  useEffect(() => {
    setHasVideoError(false);
  }, [videoFileID]);

  // Asset not yet loaded — render nothing to avoid firing an image request
  // for video assets (backend rejects /preview requests for study_dental_video).
  if (assetMediaKind === null) {
    return null;
  }

  if (isVideoAsset) {
    return (
      <div
        className={cn(styles.container, className)}
        style={{ aspectRatio: forceSquare ? 1 : 16 / 9 }}
      >
        {videoFileID &&
          (hasVideoError ? (
            <div className={styles.videoFallback} role="img" aria-label={alt}>
              <Icon name="alert" size={48} />
            </div>
          ) : (
            <>
              <video
                src={`${getFileSrc(videoFileID)}${VIDEO_POSTER_FRAGMENT}`}
                className={styles.video}
                preload="metadata"
                muted
                playsInline
                aria-label={alt}
                onError={handleVideoError}
              />
              <div className={styles.playOverlay}>
                <Icon name="play" size={16} />
              </div>
            </>
          ))}
      </div>
    );
  }

  const src = getImageSrc(photoID, size);

  if (!transformData) {
    return <img src={src} alt={alt} className={className} />;
  }

  const { rotation, flipHorizontal, cropBox, imageAspectRatio } = transformData;

  // Never zoom out in thumbnails — clamp to 1
  const zoom = Math.max(1, transformData.zoom ?? 1);
  const panX = transformData.panX ?? 0;
  const panY = transformData.panY ?? 0;

  const coverScale = getCoverScaleForContainer(rotation, imageAspectRatio);
  const totalScale = zoom * coverScale;

  // Clamp pan so the scaled image always covers the container
  const maxPan = Math.max(0, (totalScale - 1) / 2);
  const clampedPanX = Math.max(-maxPan, Math.min(maxPan, panX));
  const clampedPanY = Math.max(-maxPan, Math.min(maxPan, panY));

  const transformCSS = buildTransformCSS(
    rotation,
    flipHorizontal,
    totalScale,
    clampedPanX,
    clampedPanY,
  );

  const containerAspectRatio = forceSquare
    ? 1
    : cropBox
      ? (cropBox.width / cropBox.height) * imageAspectRatio
      : imageAspectRatio;

  const containerStyle: React.CSSProperties = {
    aspectRatio: containerAspectRatio,
  };

  return (
    <div
      className={cn(styles.container, className)}
      data-component="transformed-photo"
      style={containerStyle}
    >
      {flipHorizontal && (
        <Icon name="mirror" size={16} className={styles.editBadge} />
      )}
      <div
        className={styles.transformLayer}
        style={transformCSS ? { transform: transformCSS } : undefined}
      >
        {cropBox ? (
          <div className={styles.cropWrapper}>
            <img
              src={src}
              alt={alt}
              className={styles.croppedImage}
              style={buildCropImgStyle(cropBox)}
            />
          </div>
        ) : (
          <img src={src} alt={alt} className={styles.image} />
        )}
      </div>
    </div>
  );
};
